import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileUp, Loader2, Send, X } from "lucide-react";
import { PRODUCTS } from "@/data/content";

// Emergent preview posts to the FastAPI backend; the Hostinger build sets
// REACT_APP_QUOTE_URL=/quote.php so the form posts to the PHP handler.
const QUOTE_URL =
  process.env.REACT_APP_QUOTE_URL || `${process.env.REACT_APP_BACKEND_URL}/api/quote`;

const SPORT_OPTIONS = ["Hockey", "Soccer", "Basketball", "Rugby", "Volleyball", "Baseball", "Activewear", "Other / Multiple"];
const PRODUCT_OPTIONS = [...PRODUCTS, "Multiple / Other"];
const QUANTITY_OPTIONS = ["Under 50", "50 – 250", "250 – 1,000", "1,000+", "Not sure yet"];

const initial = {
  name: "",
  company: "",
  country: "",
  email: "",
  phone: "",
  sport: SPORT_OPTIONS[0],
  product_requirement: PRODUCT_OPTIONS[0],
  estimated_quantity: QUANTITY_OPTIONS[1],
  message: "",
  website: "", // honeypot — must stay empty for real users
};

function Field({ label, required, children }) {
  return (
    <div>
      <label className="field-label">
        {label} {required && <span className="text-ice">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setError("");
    if (!f) return setFile(null);
    if (f.size > 2 * 1024 * 1024) {
      setError("Design file is too large — max 2 MB. Larger files can be sent on WhatsApp.");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.country.trim() || !form.email.trim() || form.message.trim().length < 10) {
      setError("Please complete all required fields — the message needs at least 10 characters.");
      return;
    }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("design", file);
    setBusy(true);
    try {
      const { data } = await axios.post(QUOTE_URL, fd);
      if (data.email_sent) {
        toast.success("Enquiry sent — thank you. We'll be in touch.");
      } else {
        toast.warning("Enquiry received and saved. Our team will follow up.");
      }
      setForm(initial);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Submission failed — please try again.");
      toast.error("Submission failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="quote-form" className="space-y-5">
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        data-testid="input-website-hp"
      />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Name" required>
          <input data-testid="input-name" className="field-input" value={form.name} onChange={set("name")} placeholder="Full name" />
        </Field>
        <Field label="Company / Club">
          <input data-testid="input-company" className="field-input" value={form.company} onChange={set("company")} placeholder="Optional" />
        </Field>
        <Field label="Country" required>
          <input data-testid="input-country" className="field-input" value={form.country} onChange={set("country")} placeholder="e.g. Canada" />
        </Field>
        <Field label="Email" required>
          <input data-testid="input-email" type="email" className="field-input" value={form.email} onChange={set("email")} placeholder="name@club.com" />
        </Field>
        <Field label="WhatsApp / Phone">
          <input data-testid="input-phone" type="tel" className="field-input" value={form.phone} onChange={set("phone")} placeholder="Optional" />
        </Field>
        <Field label="Sport" required>
          <select data-testid="select-sport" className="field-input" value={form.sport} onChange={set("sport")}>
            {SPORT_OPTIONS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Field>
        <Field label="Product / Requirement" required>
          <select data-testid="select-product" className="field-input" value={form.product_requirement} onChange={set("product_requirement")}>
            {PRODUCT_OPTIONS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Field>
        <Field label="Estimated Quantity" required>
          <select data-testid="select-quantity" className="field-input" value={form.estimated_quantity} onChange={set("estimated_quantity")}>
            {QUANTITY_OPTIONS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Message" required>
        <textarea
          data-testid="textarea-message"
          rows={5}
          className="field-input resize-y"
          value={form.message}
          onChange={set("message")}
          placeholder="Colors, sizes, target date, anything else we should know…"
        />
      </Field>
      <Field label="Design / Reference Upload">
        <div className="border border-dashed border-slate-300 rounded-md p-4 bg-slate-50">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="design-upload-dropzone">
            <span className="rounded-md bg-white border border-slate-200 p-2.5">
              <FileUp size={18} className="text-icehover" />
            </span>
            <span className="text-sm text-slate-600">
              {file ? file.name : "Attach artwork or a reference (image or PDF, max 2 MB)"}
            </span>
            <input
              ref={fileRef}
              data-testid="input-design-upload"
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={onFile}
            />
            {file && (
              <button
                type="button"
                data-testid="design-upload-clear"
                onClick={(e) => {
                  e.preventDefault();
                  setFile(null);
                  if (fileRef.current) fileRef.current.value = "";
                }}
                className="ml-auto text-slate-400 hover:text-navy"
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            )}
          </label>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Larger files? Send them on WhatsApp or by email.</p>
      </Field>
      {error && (
        <p data-testid="quote-form-error" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <button type="submit" disabled={busy} data-testid="quote-form-submit-button" className="btn-accent w-full sm:w-auto disabled:opacity-60">
        {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
        {busy ? "Sending…" : "Submit Enquiry"}
      </button>
    </form>
  );
}
